import { SqlOrder } from '@/enums/SqlOrder'
import type { IFieldSearchOptions } from '@/interfaces/api/IFieldSearchOptions'
import type { IQueryFilter } from '@/interfaces/api/IQueryFilter'
import type { IQueryOptions } from '@/interfaces/api/IQueryOptions'
import { ISort } from '@/interfaces/api/ISort'
import type { ITableOptions } from '@/interfaces/ITableOptions'
import type { ITableSort } from '@/interfaces/ITableSort'

/**
 * Convert a TableSort to an orderBy. The sort for the UI is different fro mthe database.
 * @param sortBy A UI sort collection.
 * @returns
 */
export const convertTableSortToOrderBy = (sortBy: Array<ITableSort>): Array<ISort> => {
  if (!Array.isArray(sortBy) || sortBy.length === 0) {
    return []
  }

  return sortBy.map((item: ITableSort) => {
    if (item.key === '' || (item.order !== 'asc' && item.order !== 'desc')) {
      throw new Error('Invalid sort object: key and order must be defined.')
    }
    const sqlOrder = item.order.toLowerCase() === 'desc' ? SqlOrder.DESC : SqlOrder.ASC
    return { field: item.key, order: sqlOrder }
  })
}

/**
 * Parses a field search into a field search options object.
 * @param tableOptions DataTable options.
 * @param searchFields Collection of fields to search.
 * @param whereClause The where clause to apply to the search.
 * @returns
 */
export const parseFieldSearch = (
  tableOptions: ITableOptions,
  searchFields: Array<string>,
  whereClause?: Array<IQueryFilter>
): IFieldSearchOptions => {
  const fieldSearchOptions: IFieldSearchOptions = {
    currentPage: tableOptions.page,
    itemsPerPage: tableOptions.itemsPerPage,
    fields: searchFields,
    text: tableOptions.search,
    orderBy: convertTableSortToOrderBy(tableOptions.sortBy)
  }
  if (whereClause) {
    fieldSearchOptions.where = whereClause
  }
  return fieldSearchOptions
}

/**
 * Applies table options to an existing query options object.
 * @param tableOptions DataTable options.
 * @param itemQuery Existing query options.
 * @returns
 */
export const applyTableOptions = (
  tableOptions: ITableOptions,
  itemQuery: IQueryOptions
): IQueryOptions => {
  const queryOptions: IQueryOptions = { ...itemQuery }
  queryOptions.limit = tableOptions.itemsPerPage
  queryOptions.offset = Math.floor(tableOptions.itemsPerPage * (tableOptions?.page - 1))
  queryOptions.orderBy = convertTableSortToOrderBy(tableOptions.sortBy)
  return queryOptions
}
